import {
  BasicButton,
  DialogContent,
  DialogFooter,
  Modal,
  PrimaryButton,
  DialogHeader,
  Dialog,
  Stack,
  Text,
} from "@certn/cascadia";
import {
  RadioGroup,
  Radio,
  Divider,
  Select,
  SelectItem,
  DataDisplay,
  Picker,
} from "@certn/cascadia-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  CodeOption,
  Code,
  ModalSaveData,
} from "../../types/canadian-criminal-record-types";

interface CCRCEditModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  code: CodeOption;
  existingCodeData: Code | null;
  handleSave: (data: ModalSaveData) => void;
}

export function CCRCEditModal({
  isOpen,
  onOpenChange,
  code,
  existingCodeData,
  handleSave,
}: CCRCEditModalProps) {
  const { t } = useTranslation();

  // Initialize pickerSelections from existingCodeData
  const initializePickerSelections = (): Record<string, string> => {
    if (!existingCodeData) return {};

    const selections: Record<string, string> = {};

    selections["1 offence-0_3_years"] =
      existingCodeData.one_offense.zero_to_three_years_since_offense_outcome;

    selections["1 offence-3_10_years"] =
      existingCodeData.one_offense.three_to_ten_years_since_offense_outcome;

    selections["1 offence-10_plus_years"] =
      existingCodeData.one_offense.ten_or_more_years_since_offense_outcome;

    selections["2+ offences-0_3_years"] =
      existingCodeData.two_or_more_offenses.zero_to_three_years_since_offense_outcome;

    selections["2+ offences-3_10_years"] =
      existingCodeData.two_or_more_offenses.three_to_ten_years_since_offense_outcome;

    selections["2+ offences-10_plus_years"] =
      existingCodeData.two_or_more_offenses.ten_or_more_years_since_offense_outcome;

    return selections;
  };

  // Determine initial selectedOutcome based on existing data
  const getInitialOutcome = () => {
    if (!existingCodeData) return null;

    // Check if global outcome is set
    if (
      existingCodeData.global_outcome &&
      existingCodeData.global_outcome !== "NOT_SET"
    ) {
      return "GLOBAL";
    }

    // Check if any conditional values are set
    const initialSelections = initializePickerSelections();
    const hasConditionalValues = Object.values(initialSelections).some(
      (value) => value && value !== "NOT_SET"
    );

    return hasConditionalValues ? "CONDITIONAL" : null;
  };

  const [selectedOutcome, setSelectedOutcome] = useState<string | null>(
    getInitialOutcome()
  );
  const [pickerSelections, setPickerSelections] = useState<
    Record<string, string>
  >(initializePickerSelections());
  const [globalOutcome, setGlobalOutcome] = useState<string | undefined>(
    existingCodeData?.global_outcome !== "NOT_SET"
      ? existingCodeData?.global_outcome
      : undefined
  );

  const pickerItems = [
    {
      id: "REVIEW",
      name: t("Review"),
    },
    {
      id: "CLEAR",
      name: t("Clear"),
    },
    {
      id: "CRITERIA_NOT_MET",
      name: t("Not Met"),
    },
  ];

  const getPickerLabel = (key: string) => {
    const selectedId = pickerSelections[key];
    if (!selectedId || selectedId === "NOT_SET") return t("Not Set");
    const selectedItem = pickerItems.find((item) => item.id === selectedId);
    return selectedItem ? selectedItem.name : t("Select outcome");
  };
  return (
    <Modal testID="edit-modal" isOpen={isOpen} onOpenChange={onOpenChange}>
      <Dialog>
        <DialogHeader onClose={() => onOpenChange(false)}>
          {code.label}
        </DialogHeader>
        <DialogContent>
          <Stack gap="space-4">
            <Text style="global/heading-medium">
              {t("Offence Description")}
            </Text>
            <Text>{code.description || t("No description available")}</Text>

            <Divider />
            <Text style="global/heading-medium">{t("Select Outcome")}</Text>
            <RadioGroup
              value={selectedOutcome || undefined}
              onChange={(value) => {
                setSelectedOutcome(value);
              }}
            >
              <Radio value="GLOBAL" testID={"global-outcome-radio"} isCard>
                <Stack gap="space-1">
                  <Text style="global/heading-small">
                    {t("Global outcome")}
                  </Text>
                  <Text style="input/helper">
                    {t("Assigns the overall result of the record check")}
                  </Text>
                </Stack>
              </Radio>
              <Radio
                value="CONDITIONAL"
                testID={"conditional-outcome-radio"}
                isCard
              >
                <Stack gap="space-1">
                  <Text style="global/heading-small">
                    {t("Set conditional")}
                  </Text>
                  <Text style="input/helper">
                    {t("Assign offence conditions by count and age")}
                  </Text>
                </Stack>
              </Radio>
            </RadioGroup>
            {selectedOutcome &&
              (selectedOutcome === "GLOBAL" ? (
                <>
                  <Divider />
                  <Text style="global/heading-medium">
                    {t("Global outcome")}
                  </Text>
                  <Select
                    name="global-outcome"
                    label={t("Set outcome for all")}
                    testID={"global-outcome-select"}
                    isRequired
                    defaultSelectedKey={globalOutcome}
                    onSelectionChange={(key) => {
                      setGlobalOutcome(key as string);
                    }}
                  >
                    <SelectItem id="REVIEW">{t("Review")}</SelectItem>
                    <SelectItem id="CLEAR">{t("Clear")}</SelectItem>
                    <SelectItem id="CRITERIA_NOT_MET">
                      {t("Does not meet")}
                    </SelectItem>
                  </Select>
                </>
              ) : (
                <>
                  <Divider />
                  <DataDisplay
                    rows={[
                      {
                        no_of_offenses: "1 offence",
                        key: "1",
                      },
                      {
                        no_of_offenses: "2+ offences",
                        key: "2",
                      },
                    ]}
                    columns={[
                      {
                        key: "no_of_offenses",
                        name: t("No. of offences"),
                        isRowHeader: true,
                        render: (row) => {
                          return (
                            <Text style="global/button-medium">
                              {row.no_of_offenses}
                            </Text>
                          );
                        },
                      },
                      {
                        key: "0_3_years",
                        name: t("0-3 years"),
                        isRowHeader: true,
                        render: (row) => {
                          const pickerKey = `${row.no_of_offenses}-0_3_years`;
                          return (
                            <Picker
                              items={pickerItems}
                              label={getPickerLabel(pickerKey)}
                              selectionMode="single"
                              selectedKeys={
                                pickerSelections[pickerKey] &&
                                pickerSelections[pickerKey] !== "NOT_SET"
                                  ? [pickerSelections[pickerKey]]
                                  : []
                              }
                              onSelectionChange={(keys) => {
                                const selectedKey = Array.from(keys)[0];
                                setPickerSelections((prev) => ({
                                  ...prev,
                                  [pickerKey]: selectedKey as string,
                                }));
                              }}
                            />
                          );
                        },
                      },
                      {
                        key: "3_10_years",
                        name: t("3-10 years"),
                        isRowHeader: true,
                        render: (row) => {
                          const pickerKey = `${row.no_of_offenses}-3_10_years`;
                          return (
                            <Picker
                              items={pickerItems}
                              label={getPickerLabel(pickerKey)}
                              selectionMode="single"
                              selectedKeys={
                                pickerSelections[pickerKey] &&
                                pickerSelections[pickerKey] !== "NOT_SET"
                                  ? [pickerSelections[pickerKey]]
                                  : []
                              }
                              onSelectionChange={(keys) => {
                                const selectedKey = Array.from(keys)[0];
                                setPickerSelections((prev) => ({
                                  ...prev,
                                  [pickerKey]: selectedKey as string,
                                }));
                              }}
                            />
                          );
                        },
                      },
                      {
                        key: "10_plus_years",
                        name: t("10+ years"),
                        isRowHeader: true,
                        render: (row) => {
                          const pickerKey = `${row.no_of_offenses}-10_plus_years`;
                          return (
                            <Picker
                              items={pickerItems}
                              label={getPickerLabel(pickerKey)}
                              selectionMode="single"
                              selectedKeys={
                                pickerSelections[pickerKey] &&
                                pickerSelections[pickerKey] !== "NOT_SET"
                                  ? [pickerSelections[pickerKey]]
                                  : []
                              }
                              onSelectionChange={(keys) => {
                                const selectedKey = Array.from(keys)[0];
                                setPickerSelections((prev) => ({
                                  ...prev,
                                  [pickerKey]: selectedKey as string,
                                }));
                              }}
                            />
                          );
                        },
                      },
                    ]}
                    testId={""}
                  ></DataDisplay>
                </>
              ))}
          </Stack>
        </DialogContent>
        <DialogFooter>
          <BasicButton onPress={() => onOpenChange(false)}>
            {t("Cancel")}
          </BasicButton>
          <PrimaryButton
            onPress={() => {
              handleSave({
                pickerSelections,
                globalOutcome:
                  selectedOutcome === "GLOBAL" ? globalOutcome : undefined,
                selectedOutcome,
              });
              onOpenChange(false);
            }}
          >
            {t("Set")}
          </PrimaryButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
